import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  ElementRef,
  ViewChild,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  inject,
  ChangeDetectorRef,
  Self,
  Optional,
  OnChanges,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  NgControl,
  ValidationErrors,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IconComponent } from '../../icons';

/**
 * Component to wrap the native input element with Tailwind styling and
 * a11y handling for errors and helper text.
 *
 * @see `TextareaComponent`
 *
 * @usageNotes
 * Make sure to set the `inputId` and not `id` or else you will have strange behavior.
 */
@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'da-input',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, IconComponent],
    templateUrl: './text-input.component.html'
})
export class InputComponent implements ControlValueAccessor, OnChanges, OnInit {
  /** String with space delimiters between ids to associate with the input using aria-describedby */
  @Input() public describedByIds?: string;
  /** Label for the input (required) */
  @Input() public labelText!: string;
  /** Additional guidance text, displays below the input line */
  @Input() public helperText?: string;
  /** ValidationErrors passed in (e.g. from a FormGroup) to manage error messages.  Without input it defaults to using whatever errors are on the default ngcontrol. */
  @Input() public errorOverrides?: ValidationErrors;
  /** Size mode ('normal' or 'large', default: 'normal') */
  @Input() public size: 'normal' | 'large' = 'normal';
  /** Whether the field is required (default: false) */
  @Input() public required: boolean = false;
  /** Maximum character length (default: 50) */
  @Input() public maxLength?: number = 50;
  /** Unique identifier for the input (required) */
  @Input() public inputId!: string;
  /** Valid text-based input types that can be used (default: text) */
  @Input() public inputType:
    | 'text'
    | 'password'
    | 'email'
    | 'url'
    | 'tel'
    | 'number' = 'text';
  /** Mark the input as readonly / disabled */
  @Input() public readonly: boolean = false;
  /** Regex pattern to validate against */
  @Input() public pattern?: string | RegExp = '';

  /** Reference to the input element itself for manipulation */
  @ViewChild('textInput') public textInput!: ElementRef<HTMLInputElement>;
  /** Emits { eventType: string, event: Event } on input changes */
  @Output() public inputEvent: EventEmitter<{
    eventType: string;
    event: Event;
  }> = new EventEmitter();

  /** Current value of the input, only used when not wrapped in a form. (default: '') */
  public value: string = '';
  /** Internal state tracking of the reset button */
  public showResetButton: boolean = false;
  /** Translated label for the reset button (Core_Clear) */
  public resetButtonLabel = this.translate.instant('Core_Clear');
  /** Translated label for the readonly icon (Core_Readonly) */
  public readonlyDescription = this.translate.instant('Core_Readonly');

  private errorMessages = {
    pattern: this.translate.instant('Core_InvalidValue'),
    required: this.translate.instant('Core_FieldRequired'),
  };

  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  /**
   * Transforms ValidationErrors into a format that can be used with ngFor
   * to automatically handle error display.  Defautls to errors on the internal
   * ngControl, but will use the `errorOverrides` input instead if it exists.
   */
  public get errors(): { [key: string]: string }[] {
    // if we have no errors just return empty array
    if (!this.ngControl?.errors && !this.errorOverrides) return [];

    // otherwise we create an Angular-iterable array of error messages
    // out of the errors returned, defaulting to a default error message
    // if one matches a default error we provide a message for (e.g. required
    // or pattern), otherwise the field will display no error
    const errors = [];
    // if we pass in errors to the field (e.g. for a grouped field), prefer
    // that over the internal errors on the control.
    const validationErrors = this.errorOverrides
      ? this.errorOverrides
      : this.ngControl?.errors;
    // loop all the errors and push to an array that can be displayed on the page
    for (const [key, value] of Object.entries(validationErrors)) {
      const error = {
        name: key,
        message: value.message || this.errorMessages[key],
      };

      // create the error message object
      errors.push(error);
    }
    return errors;
  }

  /**
   * Checks if the field should be considered invalid.  Overridden by external
   * errors passed in, otherwise defaults to internal dirty/invalid check on the
   * form control associated with the input.
   */
  public get isInvalid(): boolean {
    return (
      (this.ngControl?.dirty && this.ngControl?.invalid) ||
      !!this.errorOverrides
    );
  }

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private translate: TranslateService
  ) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
  }

  public ngOnInit(): void {
    // this was the only way I could get the component to update itself if the
    // form is checked for validity. Hopefully becomes unnecessary when we refactor to signals.
    this.ngControl?.statusChanges?.subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  public ngOnChanges(): void {
    this.cdr.detectChanges();
  }

  /**
   * Resets the input field, clearing its value and resetting related properties.
   */
  public resetField(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    // prefer using form controls if they exist
    if (this.ngControl) {
      this.ngControl.reset();
    } else {
      this.value = '';
    }

    this.showResetButton = false;
    const el = this.textInput.nativeElement;
    el.focus();
  }

  public onInputChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.value = inputElement.value;
    this.showResetButton = this.value.length > 0;
    this.onChange(this.value);
  }

  public onFocus(event: Event): void {
    this.showResetButton = this.value?.length > 0;
    this.inputEvent.emit({ eventType: 'focus', event });
  }

  /**
   * Handle all the events.
   * @param eventType click, focus blur etc.
   * @param event the actual event
   */
  public handleEvent(eventType: string, event: Event) {
    this.inputEvent.emit({ eventType, event });
  }

  public writeValue(value: string): void {
    this.value = value;

    // needed to ensure things like form.reset will affect the field
    this.cdr.detectChanges();
  }

  public onChange: (value: string) => void = () => null;

  /**
   * Hide the clear/reset button.
   */
  public onTouched: (event: Event) => void = (event: Event) => {
    // Delay hiding the button to allow for click events
    // When relying on the tailwind peer selector the click event does not fire as it hides the button.
    this.inputEvent.emit({ eventType: 'focus', event });
    setTimeout(() => {
      this.showResetButton = false;
    }, 200);
  };

  /**
   * Required as ControlValueAccessor
   * @param fn
   */
  public registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  /**
   * Required as ControlValueAccessor
   * @param fn
   */
  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Required as ControlValueAccessor
   * Noop to not override readonly state
   * @param fn
   */
  public setDisabledState(): void {
    return;
  }
}
